import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { BillingFrequency } from '@/graphql/generatedTypes';
import { cn } from '@/lib/utils';
import { formatPrice } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import {
  DEFAULT_TRACKABLE_ITEM_TYPE_ICON,
  getImageUrl,
} from '@/utils/imageUrl';
import { DEFAULT_CURRENCY } from '@/utils/price';
import { formatBillingFrequency } from '../utils';
import { format } from 'date-fns';

export type AssetBillingInfo = {
  currencySymbol?: string | null;
  isBillable?: boolean | null;
  price?: number | null;
  billingFrequency?: BillingFrequency | null;
};

export type MerchantAssetCardProps = {
  removedAt?: string | null;
  assetId?: string;
  itemTypeName: string;
  imageUrl?: string | null;
  billingInfo?: AssetBillingInfo;
  className?: string;
};

function BillingBadge({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Badge
      variant="outline"
      className={cn('tw:w-fit tw:lowercase tw:font-medium', className)}
    >
      {children}
    </Badge>
  );
}

function AssetBillingInfo({ billing }: { billing: AssetBillingInfo }) {
  const isBillable = !!billing.isBillable;

  if (!isBillable) {
    return <BillingBadge>{i18n.t('InventoryCheck:nonBillable')}</BillingBadge>;
  }

  const frequencyDisplay = formatBillingFrequency(billing.billingFrequency);

  let priceDisplay = null;
  if (billing.price) {
    const currencyToDisplay =
      billing.currencySymbol ?? `${DEFAULT_CURRENCY} (?)`;
    priceDisplay = formatPrice(
      billing.price,
      null,
      false,
      false,
      currencyToDisplay,
    );
  }

  return (
    <div className="tw:flex tw:flex-wrap tw:gap-2 tw:items-center tw:w-full">
      <BillingBadge className="tw:text-green-600 tw:border-green-400">
        {i18n.t('InventoryCheck:billable')}
      </BillingBadge>
      {priceDisplay && <BillingBadge>{priceDisplay}</BillingBadge>}
      {frequencyDisplay && <BillingBadge>{frequencyDisplay}</BillingBadge>}
    </div>
  );
}

export function MerchantAssetCard({
  assetId,
  itemTypeName,
  imageUrl,
  billingInfo,
  removedAt,
  className,
  children,
}: React.PropsWithChildren<MerchantAssetCardProps>) {
  const imageSrc = getImageUrl(
    320,
    imageUrl || DEFAULT_TRACKABLE_ITEM_TYPE_ICON,
  );

  return (
    <Card
      className={cn(
        'tw:overflow-hidden tw:p-4 tw:pt-0',
        !!removedAt && 'tw:opacity-50',
        className,
      )}
    >
      {/* Image Section */}
      <div className="tw:aspect-3/2 tw:bg-background tw:flex tw:items-center tw:justify-center">
        <img
          src={imageSrc}
          alt={itemTypeName}
          className="tw:h-full tw:w-auto tw:object-contain tw:max-h-full"
        />
      </div>

      {/* Footer Section */}
      <div className="tw:flex tw:flex-col tw:gap-6">
        {/* Title Info */}
        <div className="tw:flex tw:flex-col">
          <span className="tw:text-sm tw:text-muted-foreground">
            {itemTypeName}{' '}
            {removedAt
              ? `(${i18n.t('Common:removedAt')} ${format(removedAt, 'dd.MM.yyyy')})`
              : ''}
          </span>
          {assetId && <span className="tw:font-medium">#{assetId}</span>}
        </div>

        {/* Billing Info (only for display type) */}
        {billingInfo && <AssetBillingInfo billing={billingInfo} />}

        {!!children && <div>{children}</div>}
      </div>
    </Card>
  );
}

export default MerchantAssetCard;
